import type { StoryDefaultPlatform } from 'types/story';

export type DesignModeSelection = Record<string, string>;

export type DesignModeValue<T> = {
	base: T;
	overrides?: Array<{
		when: DesignModeSelection;
		value: T;
	}>;
};

export type DesignMode = {
	id: string;
	name: string;
	colorScheme?: 'light' | 'dark';
};

export type DesignModeGroup = {
	id: string;
	name: string;
	defaultModeId: string;
	modes: DesignMode[];
};

export type DesignModeActivation =
	| { type: 'fixed'; modeId: string }
	| {
			type: 'media';
			feature: 'prefers-color-scheme';
			mapping: { light: string; dark: string };
	  };

export type StoryDesignModeSettings = {
	activation: Record<string, DesignModeActivation>;
};

export type DesignTokenType = 'color' | 'dimension' | 'fontFamily' | 'number';
export type DesignToken = { id: string; name: string; description?: string } & (
	| { type: 'color' | 'dimension' | 'fontFamily'; value: DesignModeValue<string> }
	| { type: 'number'; value: DesignModeValue<number> }
);
export type TokenCollection = { id: string; name: string; tokens: DesignToken[] };
export type DesignTokenSet = { schemaVersion: 1; collections: TokenCollection[] };

export type DesignAssetRole = 'logo' | 'compactLogo' | 'share' | 'favicon' | 'other';
export type DesignAsset = {
	id: string;
	name: string;
	role: DesignAssetRole;
	type: 'image';
	value: DesignModeValue<string>;
	description?: string;
};
export type DesignAssetSet = { schemaVersion: 1; assets: DesignAsset[] };

export const DESIGN_PRESET_PROPERTIES = [
	'fontFamily',
	'fontSize',
	'lineHeight',
	'letterSpacing',
	'fontWeight',
	'color',
	'backgroundColor',
	'textTransform',
	'textDecoration',
	'textAlign',
	'direction',
	'marginTop',
	'marginRight',
	'marginBottom',
	'marginLeft',
	'paddingTop',
	'paddingRight',
	'paddingBottom',
	'paddingLeft',
	'borderWidth',
	'borderStyle',
	'borderColor',
	'borderRadius',
	'borderTopLeftRadius',
	'borderTopRightRadius',
	'borderBottomRightRadius',
	'borderBottomLeftRadius',
	'boxShadow',
	'textShadow',
	'opacity',
] as const;
export type DesignPresetProperty = (typeof DESIGN_PRESET_PROPERTIES)[number];
export type DesignPresetValue = string | number;
export type DesignPresetPropertyValue = {
	base?: DesignPresetValue;
	platforms?: Partial<Record<StoryDefaultPlatform, DesignPresetValue>>;
};
export type DesignPreset = {
	id: string;
	name: string;
	description?: string;
	properties: Partial<Record<DesignPresetProperty, DesignPresetPropertyValue>>;
};

export type DesignDefinition = {
	schemaVersion: 1;
	modeGroups: DesignModeGroup[];
	defaults: { root: { fontFamily: DesignModeValue<string> } };
	tokens: DesignTokenSet;
	assets: DesignAssetSet;
	presets?: DesignPreset[];
};
